import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CATEGORY_KEYS, categoryMeta } from "@/lib/catalog";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "专题", to: "/specials" },
  { label: "搜索", to: "/search" },
  { label: "关于", to: "/about" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setCategoryOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCategoryOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
    setMenuOpen(false);
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-shell items-center justify-between gap-4 px-4 md:h-20 md:px-8">
        <Link to="/" className="group flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-900 text-background-50 md:h-10 md:w-10">
            <i className="ri-movie-2-line text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-[17px] font-extrabold tracking-tight text-foreground-950 md:text-[19px]">
              日韩伦理片
            </span>
            <span className="mt-1 font-label text-[9px] uppercase tracking-[0.22em] text-secondary-500">
              Film Archive
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          <Link
            to="/"
            className={`rounded-md px-3 py-2 text-sm transition-colors ${
              isActive("/")
                ? "bg-primary-50 font-semibold text-primary-800"
                : "text-foreground-700 hover:text-primary-700"
            }`}
          >
            首页
          </Link>
          <div
            className="relative"
            ref={dropdownRef}
            onMouseEnter={() => setCategoryOpen(true)}
            onMouseLeave={() => setCategoryOpen(false)}
          >
            <button
              type="button"
              onClick={() => setCategoryOpen((open) => !open)}
              className={`flex cursor-pointer items-center gap-1 rounded-md px-3 py-2 text-sm whitespace-nowrap transition-colors ${
                location.pathname.startsWith("/category")
                  ? "bg-primary-50 font-semibold text-primary-800"
                  : "text-foreground-700 hover:text-primary-700"
              }`}
            >
              分类
              <i className={`ri-arrow-down-s-line text-base transition-transform ${categoryOpen ? "rotate-180" : ""}`} />
            </button>
            {categoryOpen && (
              <div className="absolute left-0 top-full w-[320px] rounded-lg border border-background-200 bg-background-50 p-2 pt-3">
                {CATEGORY_KEYS.map((key) => (
                  <Link
                    key={key}
                    to={`/category/${encodeURIComponent(key)}`}
                    className="flex items-start gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100"
                  >
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                      <i className={`${categoryMeta[key].icon} text-base`} />
                    </span>
                    <span className="flex flex-col">
                      <span className="text-sm font-semibold text-foreground-900">
                        {categoryMeta[key].name}
                        <span className="ml-1.5 font-label text-[10px] font-normal uppercase tracking-wider text-secondary-500">
                          {categoryMeta[key].en}
                        </span>
                      </span>
                      <span className="mt-0.5 text-xs leading-relaxed text-foreground-600">
                        {categoryMeta[key].description}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
          {navLinks.slice(1).map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`rounded-md px-3 py-2 text-sm transition-colors ${
                isActive(link.to)
                  ? "bg-primary-50 font-semibold text-primary-800"
                  : "text-foreground-700 hover:text-primary-700"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={handleSearch}
            className="hidden h-10 items-center rounded-full border border-background-300 bg-background-100 pl-3 pr-1 transition-colors focus-within:border-primary-400 md:flex"
          >
            <i className="ri-search-line text-sm text-secondary-500" />
            <input
              type="search"
              name="q"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索作品、导演、主演"
              aria-label="站内搜索"
              className="w-36 bg-transparent px-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 lg:w-48"
            />
            <button
              type="submit"
              aria-label="提交搜索"
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-right-line text-sm" />
            </button>
          </form>

          <Link
            to="/search"
            aria-label="搜索"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-300 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 md:hidden"
          >
            <i className="ri-search-line text-lg" />
          </Link>

          <button
            type="button"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-300 text-foreground-800 transition-colors hover:border-primary-300 lg:hidden"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <div className="mx-auto max-w-shell px-4 py-4 md:px-8">
            <form
              onSubmit={handleSearch}
              className="mb-4 flex h-11 items-center rounded-full border border-background-300 bg-background-100 pl-3 pr-1"
            >
              <i className="ri-search-line text-base text-secondary-500" />
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索作品、导演、主演"
                aria-label="站内搜索"
                className="flex-1 bg-transparent px-2 text-sm outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                aria-label="提交搜索"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50"
              >
                <i className="ri-arrow-right-line" />
              </button>
            </form>
            <nav className="flex flex-col">
              {navLinks.slice(0, 1).map((link) => (
                <Link key={link.to} to={link.to} className="flex items-center justify-between border-b border-background-200 py-3 text-sm font-medium text-foreground-900">
                  {link.label}
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </Link>
              ))}
              <div className="border-b border-background-200 py-3">
                <p className="mb-2 font-label text-[11px] uppercase tracking-[0.18em] text-secondary-500">分类</p>
                <div className="grid grid-cols-3 gap-2">
                  {CATEGORY_KEYS.map((key) => (
                    <Link
                      key={key}
                      to={`/category/${encodeURIComponent(key)}`}
                      className="flex flex-col items-center gap-1 rounded-md border border-background-200 bg-background-100 py-3 text-xs text-foreground-800"
                    >
                      <i className={`${categoryMeta[key].icon} text-base text-primary-600`} />
                      {key}
                    </Link>
                  ))}
                </div>
              </div>
              {navLinks.slice(1).map((link) => (
                <Link key={link.to} to={link.to} className="flex items-center justify-between border-b border-background-200 py-3 text-sm font-medium text-foreground-900">
                  {link.label}
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}